/** SUNTRONIX ONE — Moments Studio.
 *  Authorised configuration of ONE MOMENTS: which milestones celebrate, the
 *  major-order threshold, celebration length and sound policy.
 *  PATCH is management-only (backend enforced) — others see a read-only view.
 */
import { useEffect, useState } from "react";
import { Sparkles, Loader2, Save, Trophy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TYPE_LABELS = {
  ORDER_WON: "Order Won / PO Received",
  MAJOR_ORDER: "Major Order",
  DEPLOYMENT_COMPLETED: "Deployment Completed",
  APPLECARE_ACTIVATED: "AppleCare Activated",
  BATCH_COMPLETED: "Large Batch Completed",
  STOCK_RECONCILED: "100% Stock Reconciliation",
  CUSTOMER_ONBOARDED: "Customer Onboarding Completed",
  RECOVERY_COMPLETED: "Significant Recovery Completed",
};
const ALL_TYPES = Object.keys(TYPE_LABELS);

const fmtINR = (n) => `\u20B9 ${Number(n || 0).toLocaleString("en-IN")}`;

export default function MomentsStudio() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    api.get("/moments/config").then(({ data }) => setCfg(data))
      .catch(() => toast.error("Could not load configuration"))
      .finally(() => setLoading(false));
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const toggleType = (t) => {
    const cur = new Set(cfg.enabled_types || []);
    cur.has(t) ? cur.delete(t) : cur.add(t);
    set({ enabled_types: Array.from(cur) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/moments/config", {
        enabled: cfg.enabled,
        enabled_types: cfg.enabled_types,
        major_order_threshold: Number(cfg.major_order_threshold) || 0,
        duration_ms: Number(cfg.duration_ms) || 3800,
        sound_policy: cfg.sound_policy,
        weekly_team_digest: !!cfg.weekly_team_digest,
        digest_recipient: cfg.digest_recipient || "",
      });
      setCfg(data);
      toast.success("Moments configuration saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Only management can change this");
    } finally { setSaving(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/moments/wins/digest/send", { to: cfg.digest_recipient || undefined });
      if (data.ok) toast.success(`Team-wins digest sent (${data.summary?.orders_won || 0} orders won)`);
      else toast.error(data.error || "Could not send — set a valid recipient");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Only management can send the digest");
    } finally { setSendingDigest(false); }
  };

  if (loading || !cfg) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-16 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading Moments Studio…
    </div>;
  }

  return (
    <div className="max-w-3xl mx-auto py-6 space-y-6" data-testid="moments-studio-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">Configuration Studio</div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Sparkles className="h-5 w-5 text-violet-600" /> Moments Studio
          </h1>
          <p className="text-sm text-slate-500 mt-1">Celebrate genuine business milestones — tastefully.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="moments-save-btn" className="bg-violet-600 hover:bg-violet-700">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <label className="flex items-center gap-3 text-sm font-medium text-slate-800" data-testid="moments-enabled-toggle">
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} />
          ONE Moments {cfg.enabled ? "enabled" : "disabled"}
        </label>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Milestones that celebrate</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ALL_TYPES.map((t) => {
            const on = (cfg.enabled_types || []).includes(t);
            return (
              <button key={t} onClick={() => toggleType(t)} data-testid={`moment-type-${t}`}
                className={"flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors " +
                  (on ? "border-violet-300 bg-violet-50" : "border-slate-200 bg-white hover:bg-slate-50")}>
                <span className="text-[13px] text-slate-800">{TYPE_LABELS[t]}</span>
                <span className={"h-4 w-4 rounded-full border grid place-items-center " + (on ? "bg-violet-600 border-violet-600" : "border-slate-300")}>
                  {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Major order threshold" hint={`Orders at or above ${fmtINR(cfg.major_order_threshold)} get a richer celebration.`}>
          <Input type="number" value={cfg.major_order_threshold}
            onChange={(e) => set({ major_order_threshold: e.target.value })}
            data-testid="moments-threshold" />
        </Field>
        <Field label="Celebration length (ms)" hint="How long the overlay stays before it minimises.">
          <Input type="number" value={cfg.duration_ms}
            onChange={(e) => set({ duration_ms: e.target.value })}
            data-testid="moments-duration" />
        </Field>
        <Field label="Sound policy" hint="Respects each user's personal setting when set to 'user'.">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
            {["user", "off"].map((v) => (
              <button key={v} onClick={() => set({ sound_policy: v })} data-testid={`moments-sound-${v}`}
                className={"px-3.5 py-2 text-[13px] font-medium capitalize " +
                  (cfg.sound_policy === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white")}>
                {v === "user" ? "Per user" : "Always off"}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-emerald-500" /> Weekly team-wins digest</div>
        <p className="text-[12px] text-slate-500 mt-0.5">An optional weekly email summarising orders won, value and the people who contributed. Off by default.</p>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-center gap-3 text-sm text-slate-700" data-testid="digest-toggle">
            <Switch checked={!!cfg.weekly_team_digest} onCheckedChange={(v) => set({ weekly_team_digest: v })} />
            {cfg.weekly_team_digest ? "Enabled" : "Disabled"}
          </label>
          <Input type="email" placeholder="Digest recipient email" value={cfg.digest_recipient || ""}
            onChange={(e) => set({ digest_recipient: e.target.value })}
            className="max-w-xs" data-testid="digest-recipient" />
          <Button variant="outline" onClick={sendDigest} disabled={sendingDigest} data-testid="digest-send-now">
            {sendingDigest ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Send now
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}
